"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronRight, Globe } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

import { usePublicProfileStore } from "../store/usePublicProfileStore";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";

export function PublicLanguagesDisplay() {
  const { profileData } = usePublicProfileStore();
  const data = profileData?.languages_section;
  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const [isExpanded, setIsExpanded] = useState(false);

  if (!data || data.length === 0) return null;

  const displayedLanguages = isExpanded ? data : data.slice(0, 4);
  const hasMore = data.length > 4;

  const getProficiencyLevel = (proficiency: string) => {
    switch (proficiency) {
      case "native":
        return 100;
      case "fluent":
        return 80;
      case "conversational":
        return 60;
      default:
        return 40;
    }
  };

  const getProficiencyColor = (proficiency: string) => {
    switch (proficiency) {
      case "native":
        return "bg-green-500";
      case "fluent":
        return "bg-blue-500";
      case "conversational":
        return "bg-yellow-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-teal-400/50 shadow-2xl backdrop-blur-xl">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br from-teal-400 to-cyan-600" />
        <div className="absolute -right-10 -bottom-10 h-32 w-32 rounded-full bg-gradient-to-tr from-cyan-400 to-teal-500" />
      </div>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl bg-teal-500/20 p-2">
            <Icon className="text-teal-400" height="1.5rem" icon="mdi:language" width="1.5rem" />
          </div>
          {t("profile:languagesSection.title")}
        </motion.h2>
      </CardHeader>
      <CardContent className="relative space-y-4">
        <div
          className={`grid grid-cols-1 gap-4 sm:grid-cols-${displayedLanguages.length >= 2 ? "2" : "1"}`}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {displayedLanguages.map((language, index) => (
              <motion.div
                key={language.id}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="relative overflow-hidden rounded-2xl border border-teal-300/20 bg-teal-500/10 backdrop-blur-sm transition-all duration-300 hover:bg-teal-500/15"
                exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <div className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-teal-500/20 p-2">
                        <Globe className="h-4 w-4 text-teal-400" />
                      </div>
                      <h3 className="text-accent-foreground font-semibold">{language.name}</h3>
                    </div>
                    <Badge
                      className="border-teal-400/30 bg-teal-500/20 text-xs font-medium text-teal-300"
                      variant="outline"
                    >
                      {language.proficiency_display}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-teal-500/20">
                      <motion.div
                        animate={{ width: `${getProficiencyLevel(language.proficiency_slug)}%` }}
                        className={`${getProficiencyColor(language.proficiency_slug)} h-full rounded-full`}
                        initial={{ width: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                      />
                    </div>
                    <div className="text-muted-foreground flex justify-between text-xs">
                      <span>Beginner</span>
                      <span>Native</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        {hasMore && (
          <motion.div
            animate={{ opacity: 1 }}
            className="pt-2"
            initial={{ opacity: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button
              className="mt-4 w-full justify-center gap-2 rounded-xl border-teal-400/30 bg-teal-500/20 text-teal-300 hover:bg-teal-500/30"
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span>
                {isExpanded ? t("common:showLess") : t("common:showAll", { count: data.length })}
              </span>
              <ChevronRight
                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
              />
            </Button>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
